import { Leaf, AlertTriangle } from "lucide-react";
import { useBrand } from "@/context/BrandContext";

const NutrientPill = ({ label, value, unit }) => {
  if (value === null || value === undefined) return null;
  return (
    <div className="rounded-[16px] bg-sageSoft px-1.5 py-2 text-center">
      <p className="font-heading text-[14px] font-black leading-none text-cocoa">
        {value}
        <span className="text-[10px] font-bold text-cocoa/75">{unit}</span>
      </p>
      <p className="mt-1 text-[9px] font-extrabold uppercase leading-none tracking-[0.06em] text-cocoa/70">
        {label}
      </p>
    </div>
  );
};

export const MealCard = ({ meal, index = 0 }) => {
  const brand = useBrand();
  const ageLabels = meal.ages
    .map((id) => brand.ageRanges.find((a) => a.id === id)?.label || id)
    .join(" · ");

  return (
    <article
      data-testid={`meal-card-${meal.day.toLowerCase()}`}
      className="mbm-card mbm-card-hover flex flex-col overflow-hidden"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-creamDeep">
        <img
          src={meal.image}
          alt={meal.name}
          loading={index < 2 ? "eager" : "lazy"}
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-700 hover:scale-[1.04]"
        />
        <span className="absolute left-4 top-4 rounded-full bg-cream/95 px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-[0.14em] text-cocoa backdrop-blur">
          {meal.day}
        </span>
        <span className="absolute right-4 top-4 rounded-full bg-amber px-3.5 py-1.5 text-[11px] font-extrabold text-cocoa">
          {meal.label}
        </span>
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <h3 className="font-heading text-lg font-semibold leading-snug">{meal.name}</h3>
        <p className="mt-2.5 text-sm leading-relaxed text-cocoa/70">{meal.description}</p>

        <div className="mt-5 grid grid-cols-3 gap-2 sm:grid-cols-5">
          <NutrientPill label="kcal" value={meal.nutrition.calories} unit="" />
          <NutrientPill label="Protein" value={meal.nutrition.protein} unit="g" />
          <NutrientPill label="Carbs" value={meal.nutrition.carbs} unit="g" />
          <NutrientPill label="Fat" value={meal.nutrition.fat} unit="g" />
          <NutrientPill label="Fibre" value={meal.nutrition.fibre} unit="g" />
        </div>
        {(meal.nutrition.carbs === null || meal.nutrition.fat === null || meal.nutrition.calories === null) && (
          <p className="mt-2 text-[11px] text-cocoa/75">
            Some values are not published for this meal until they are confirmed.
          </p>
        )}

        <dl className="mt-5 space-y-3 border-t border-line pt-5 text-sm">
          {(meal.parentNote || meal.kidNote) && (
            <div className="grid gap-3 sm:grid-cols-2">
              {meal.parentNote && (
                <div className="rounded-[20px] bg-sageSoft p-4">
                  <dt className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-cocoa/70">
                    Parent note
                  </dt>
                  <dd className="mt-1.5 text-[13px] leading-relaxed text-cocoa/70">
                    {meal.parentNote}
                  </dd>
                </div>
              )}
              {meal.kidNote && (
                <div className="rounded-[20px] bg-amber/15 p-4">
                  <dt className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-coralInk">
                    For your child
                  </dt>
                  <dd className="mt-1.5 text-[13px] font-medium leading-relaxed text-cocoa/75">
                    {meal.kidNote}
                  </dd>
                </div>
              )}
            </div>
          )}
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
              Ingredients
            </dt>
            <dd className="mt-1.5 flex flex-wrap gap-1.5">
              {meal.ingredients.map((ing) => (
                <span
                  key={ing}
                  className="rounded-full bg-cream px-3 py-1 text-[12px] font-semibold text-cocoa/75"
                >
                  {ing}
                </span>
              ))}
            </dd>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
                Portion options
              </dt>
              <dd className="mt-1 font-medium text-cocoa">{ageLabels}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
                Availability
              </dt>
              <dd className="mt-1 font-medium text-coralInk">{meal.availability}</dd>
            </div>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
              Contains
            </dt>
            <dd className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {meal.vegetarian && (
                <span className="inline-flex items-center gap-1 rounded-full bg-sage px-3 py-1 text-[12px] font-bold text-cocoa">
                  <Leaf className="h-3 w-3" aria-hidden="true" /> Vegetarian
                </span>
              )}
              {meal.allergens.map((a) => (
                <span
                  key={a}
                  className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-[12px] font-bold"
                  style={{ background: "rgba(196,54,56,0.12)", color: "#9E2B2D" }}
                >
                  <AlertTriangle className="h-3 w-3" aria-hidden="true" /> {a}
                </span>
              ))}
            </dd>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
                Storage
              </dt>
              <dd className="mt-1 text-[13px] leading-relaxed text-cocoa/80">{meal.storage}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-semibold uppercase tracking-[0.12em] text-cocoa/70">
                Reheating
              </dt>
              <dd className="mt-1 text-[13px] leading-relaxed text-cocoa/80">{meal.reheating}</dd>
            </div>
          </div>
        </dl>
      </div>
    </article>
  );
};

export default MealCard;
